import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import BarChartIcon from '@material-ui/icons/BarChart';
import ExploreIcon from '@material-ui/icons/Explore';
import HomeIcon from '@material-ui/icons/Home';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import WarningIcon from '@material-ui/icons/Warning';
import { CentralProviderBadgeSidebar, usePermissions, useTranslation } from '@michelin/central-provider';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import { auth } from 'Auth';
import { getRestApiVersion, getVersion } from 'components/Util/version';
import { useProductTour } from 'contexts/ProductTourContext';
import GLOBAL_CONFIG from 'global-variables';
import { useHistory, useLocation, useParams } from 'react-router-dom';

const useStyles = makeStyles(() => ({
  tourLauncherIcon: {
    color: '#FCE500',
  },
  tourLauncherLabel: {
    color: '#FCE500',
    fontWeight: 700,
  },
  requestServiceTooltip: {
    backgroundColor: '#212121',
    fontSize: '1rem',
  },
  requestServiceTooltipArrow: {
    color: '#212121',
  },
}));

function Icon({ file, selected }: { file: string; selected: boolean }) {
  const theme = useTheme();
  const [svgUrl, setSvgUrl] = useState<string>('');

  const fillColor = selected ? theme.palette.text.primary : theme.palette.text.secondary;

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL}/static/images/preferencesIcons/${file}.svg`;
    fetch(url)
      .then((res) => res.text())
      .then((svgText) => {
        // Replace the fill color in the SVG with the appropriate color based on selection
        const coloredSvg = svgText.replace(/(\.st1\s*\{[^}]*fill:)#[0-9a-fA-F]{3,8}/, `$1${fillColor}`);
        const encoded = encodeURIComponent(coloredSvg).replace(/'/g, '%27').replace(/"/g, '%22');
        setSvgUrl(`data:image/svg+xml;charset=UTF-8,${encoded}`);
      });
  }, [file, selected, fillColor]);

  return <img src={svgUrl} alt={file} style={{ width: 26, marginRight: 5, marginLeft: -3 }} />;
}

// The Sidebar.Item `label` type is `string`, but MUI's ListItemText renders any
// ReactNode fine at runtime. This wraps a label with an id used to anchor the
// product tour to specific sidebar entries.
function withTourId(id: string, label: React.ReactNode): string {
  return (
    <>
      {label}
      <span
        id={id}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      />
    </>
  ) as unknown as string;
}

// Colors an item's icon/label yellow
function withHighlight(label: React.ReactNode, classes: ReturnType<typeof useStyles>): string {
  return (<span className={classes.tourLauncherLabel}>{label}</span>) as unknown as string;
}

function getSelectedItemFromPath(pathname: string): string {
  if (pathname.includes('/tire-wheel')) return 'Tire & Wheel Preferences';
  if (pathname.includes('/mechanical')) return 'Mechanical Preferences';
  if (pathname.includes('/contacts')) return 'Contacts';
  if (pathname.includes('/my-admin')) return 'My Admin Area';
  if (pathname.includes('/liveTrack')) return 'LiveTrack';
  if (pathname.includes('/request-service')) return 'Request Service';
  return 'Home';
}

export default function SideBar() {
  const classes = useStyles();
  const [sidebarOpen, setSidebarOpen] = useState(
    !document.cookie.split('; ').some((cookie) => cookie === 'sidebar-minimized=true'),
  );
  const history = useHistory();
  const location = useLocation();
  const permissions = usePermissions();
  const { t } = useTranslation();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const accountNumber = permissions.location?.customer_number || selectedAccount;
  const selectedItem = getSelectedItemFromPath(location.pathname);
  const [frontendVersion, setFrontendVersion] = useState<string>('');
  const [backendVersion, setBackendVersion] = useState<string>('');
  const [restApiVersion, setRestApiVersion] = useState<string>('...');
  const { isTourActive, startTour } = useProductTour();
  // Don't auto-collapse the sidebar while the product tour is navigating between pages
  const shouldCollapse = selectedItem !== 'Home' && !isTourActive;

  useEffect(() => {
    setFrontendVersion(getVersion());
    setBackendVersion(auth.getApiVersion() || '');
    getRestApiVersion()
      .then(setRestApiVersion)
      .catch(() => setRestApiVersion('...'));
  }, []);

  const items: CentralProviderBadgeSidebar.Item[] = [
    {
      heading: true,
      label: t('Main Menu'),
    },
    {
      label: withTourId('tour-step-home', t('Home')),
      icon: <HomeIcon />,
      selected: selectedItem === 'Home',
      onClick: () => history.push(`/${accountNumber}/home`),
      badge: 0,
    },
    {
      label: withTourId(
        'tour-step-request-service',
        sidebarOpen ? (
          <>
            <span style={{ paddingRight: 24 }}>{t('Request Service')}</span>
            <Tooltip
              title={String(t('Coming soon in MyPortal'))}
              placement="right"
              arrow
              classes={{ tooltip: classes.requestServiceTooltip, arrow: classes.requestServiceTooltipArrow }}
            >
              <InfoOutlinedIcon
                aria-label={String(t('Coming soon in MyPortal'))}
                fontSize="small"
                style={{ position: 'absolute', right: 12, top: 12, zIndex: 2 }}
              />
            </Tooltip>
          </>
        ) : (
          `${t('Request Service')}: ${String(t('Coming soon in MyPortal'))}`
        ),
      ),
      icon: <WarningIcon />,
      selected: selectedItem === 'Request Service',
      onClick: () => history.push(`/${accountNumber}/request-service`),
      badge: 0,
    },
    {
      label: withTourId('tour-step-livetrack', t('LiveTrack')),
      icon: <MyLocationIcon />,
      selected: selectedItem === 'LiveTrack',
      onClick: () => history.push(`/${accountNumber}/liveTrack`),
      badge: 0,
    },
    {
      label: withTourId('tour-step-fleet-business-insights', t('Fleet Business Insights')),
      icon: <BarChartIcon />,
      selected: selectedItem === 'Fleet Business Insights',
      onClick: () => GLOBAL_CONFIG.fbiUrl && window.open(GLOBAL_CONFIG.fbiUrl),
      badge: 0,
    },
    {
      heading: true,
      label: t('Preferences'),
    },
    {
      label: withTourId('tour-step-tire-preferences', t('Tire & Wheel Preferences')),
      icon: <Icon file="bulk-order" selected={selectedItem === 'Tire & Wheel Preferences'} />,
      selected: selectedItem === 'Tire & Wheel Preferences',
      onClick: () => history.push(`/${accountNumber}/tire-wheel`),
      badge: 0,
    },
    {
      label: withTourId('tour-step-mechanical-preferences', t('Mechanical Preferences')),
      icon: <Icon file="mechanical" selected={selectedItem === 'Mechanical Preferences'} />,
      selected: selectedItem === 'Mechanical Preferences',
      onClick: () => history.push(`/${accountNumber}/mechanical`),
      badge: 0,
    },
    {
      label: withTourId('tour-step-contacts', t('Contacts')),
      icon: <Icon file="contacts" selected={selectedItem === 'Contacts'} />,
      selected: selectedItem === 'Contacts',
      onClick: () => history.push(`/${accountNumber}/contacts`),
      badge: 0,
    },
    {
      label: withTourId('tour-step-my-admin', t('My Admin Area')),
      icon: <Icon file="all-preferences" selected={selectedItem === 'My Admin Area'} />,
      selected: selectedItem === 'My Admin Area',
      onClick: () => history.push(`/${accountNumber}/my-admin`),
      badge: 0,
    },
    {
      label: withHighlight(t('Platform Tour'), classes),
      icon: <ExploreIcon className={classes.tourLauncherIcon} />,
      position: 'bottom',
      onClick: startTour,
      badge: 0,
    },
  ];

  const versions = [
    {
      application: t('UI'),
      version: frontendVersion,
    },
    {
      application: t('API'),
      version: `GQL-${backendVersion} / REST-${restApiVersion}`,
    },
  ];

  return (
    <CentralProviderBadgeSidebar
      versions={versions}
      items={items}
      collapse={shouldCollapse}
      onShrink={() => setSidebarOpen(false)}
      onGrow={() => setSidebarOpen(true)}
      showCopyright={!runningOnCanvas()}
      showLegalInformation={false}
      showApplicationSelector={!runningOnCanvas()}
    />
  );
}
